// Data for the list and the actions on it. Lists come from paginated queries
// (one subscription per page of PAGE rows). A bounded initial load fills filtered views;
// every view can continue on demand, and exhaustion is explicit.
import { useEffect, useMemo, useRef, useState } from "react";
import { applyFilter, inLibrary, MAX_TOPICS, orderCategories, orderRecent, orderRows, topicKey, topicLabel, topicName, UNCATEGORIZED, type Scope, type TopicEdit } from "@vladrss/core";
import { api, useOwnerMutation, useOwnerPages, useOwnerQuery } from "./api";
import { optimisticTopic, optimisticCategoryOrder, optimisticFeedCategory } from "./settings";
import { usePrefs } from "./prefs";
import { command } from "./command";
import { retainArticle, retainView, viewSnapshot, beginArticleEdit, currentViewContext, retargetView, finishArticleEdit, forgetArticles, getState, reconcileSources, remember, rowCache, setView, toast, update, useStore, withOverlay, type Row } from "./store";

/**
 * Rows per list page. A change to any row (read, a saved position, full text arriving) makes
 * the server read that row's whole page again, so pages stay small: 30 rows cost 40% less
 * per change than 50, and still fill a tall screen twice.
 */
const PAGE = 30;

const AUTOLOAD_BUDGET = 300;

export type Feed = NonNullable<ReturnType<typeof useFeeds>>[number];

/** Sources include Convex's pending optimistic edits. */
export function useFeeds() {
  const feeds = useOwnerQuery(api.feeds.list, {});
  const overlay = useStore((st) => st.overlay);
  useEffect(() => { if (feeds !== undefined) reconcileSources(feeds); }, [feeds, overlay]);
  return feeds;
}
export function useSettings() { return useOwnerQuery(api.settings.get, {}); }

/** Unknown/paused keeps a last-known row; authoritative null retires it everywhere. */
export function useArticleRow(id: string): Row | null | undefined {
  useEffect(() => retainArticle(id), [id]);
  const live = useOwnerQuery(api.articles.get, { id: id as never }) as Row | null | undefined;
  const feeds = useFeeds();
  const overlay = useStore((st) => st.overlay);
  const removed = useStore((st) => st.removed);
  const effective = live && withOverlay(live);
  const sourceGone = effective && !inLibrary(effective.isSaved, feeds !== undefined && !feeds.some((f) => f.id === effective.feedId && !f.deleted), effective.retiredAt !== undefined);
  useEffect(() => {
    if (live === null) { forgetArticles([id]); return; }
    if (sourceGone) { forgetArticles([id], "source"); return; }
    if (!live) return;
    const known = rowCache.has(id);
    remember([live]);
    // An address-opened article can now join its list and reading session.
    if (!known && getState().reading?.session.length === 1) update({ keep: new Set(getState().keep) });
  }, [live, id, overlay, sourceGone]);
  if (sourceGone) return null;
  if (removed.has(id) && !(removed.get(id) === "source" && live?.isSaved)) return null;
  return live === undefined ? rowCache.get(id) : live;
}

/** Counts per source with pending optimistic changes applied, summed for categories and All; each topic's unread count likewise. */
export function useCounts() {
  const raw = useOwnerQuery(api.articles.counts, {});
  const rawTopics = useOwnerQuery(api.topics.counts, {});
  const feeds = useFeeds();
  const overlay = useStore((st) => st.overlay);
  return useMemo(() => {
    const byFeed = new Map<string, { unread: number; saved: number; total: number; unreadLeaving: number | null }>();
    for (const c of raw ?? []) byFeed.set(c.feedId, { unread: c.unread, saved: c.saved, total: c.total, unreadLeaving: c.unreadLeaving });
    if (raw !== undefined) for (const f of feeds ?? []) if (!byFeed.has(f.id)) byFeed.set(f.id, { unread: 0, saved: 0, total: 0, unreadLeaving: 0 });
    const byTopic = new Map((rawTopics ?? []).map((t) => [topicKey(t.name), t.unread]));
    const topicTotal = new Map((rawTopics ?? []).map((t) => [topicKey(t.name), t.total]));
    for (const [id, o] of overlay) {
      const r = rowCache.get(id); if (!r) continue;
      if (o.isRead !== undefined && o.isRead !== r.isRead) {
        for (const t of r.topics ?? []) { const n = byTopic.get(t); if (n !== undefined) byTopic.set(t, Math.max(0, n + (o.isRead ? -1 : 1))); }
      }
      const c = byFeed.get(r.feedId); if (!c) continue;
      if (c.unreadLeaving !== null) c.unreadLeaving += Number(!(o.isRead ?? r.isRead) && !(o.isSaved ?? r.isSaved)) - Number(!r.isRead && !r.isSaved);
      if (o.isRead !== undefined && o.isRead !== r.isRead) c.unread += o.isRead ? -1 : 1;
      if (o.isSaved !== undefined && o.isSaved !== r.isSaved) c.saved += o.isSaved ? 1 : -1;
    }
    const live = (feeds ?? []).filter((f) => !f.deleted);
    const sum = (ids: string[]) => ids.reduce((a, id) => { const c = byFeed.get(id); return c ? { unread: a.unread + c.unread, saved: a.saved + c.saved, total: a.total + c.total } : a; }, { unread: 0, saved: 0, total: 0 });
    const forScope = (scope: Scope) =>
      scope.type === "feed" ? sum([scope.feedId])
      : scope.type === "category" ? sum(live.filter((f) => f.category === scope.name).map((f) => f.id))
      : scope.type === "topic" ? { unread: byTopic.get(topicKey(scope.name)) ?? 0, saved: 0, total: topicTotal.get(topicKey(scope.name)) ?? 0 }
      : sum([...byFeed.keys()]);
    return { ready: raw !== undefined && feeds !== undefined, forScope, byFeed };
  }, [raw, rawTopics, feeds, overlay]);
}

const NONE: string[] = [];

/** The categories in the owner's order (a new order still on its way included). */
export function useCategories(): string[] {
  const feeds = useFeeds();
  const order = useSettings()?.categories ?? NONE;
  return useMemo(() => orderCategories((feeds ?? []).filter((f) => !f.deleted).map((f) => f.category), order), [feeds, order]);
}

/**
 * Shaping the sources: moving one into another category, putting categories in order, renaming
 * one (renaming into a name that exists merges the two). Each change offers Undo.
 */
export function useCategoryActions() {
  const cats = useCategories();
  const feeds = useFeeds();
  const setOrder = useOwnerMutation(api.settings.moveCategoryBefore, optimisticCategoryOrder);
  const renameCat = useOwnerMutation(api.feeds.renameCategoryResult);
  const undoRename = useOwnerMutation(api.feeds.undoCategoryRename);
  const setCategory = useOwnerMutation(api.feeds.setCategory, optimisticFeedCategory);
  const failure = "Couldn't change categories · try again";
  const placeCategory = (name: string, before: string | null) => { void command(() => setOrder({ name, before }), failure); };
  // the order as placed: every category but Uncategorized, which always comes last
  const placed = cats.filter((c) => c !== UNCATEGORIZED);

  const reorder = (from: number, to: number) => {
    if (from === to || from < 0 || from >= placed.length || to < 0 || to >= placed.length) return;
    const next = [...placed];
    next.splice(to, 0, ...next.splice(from, 1));
    placeCategory(placed[from]!, next[to + 1] ?? null);
  };
  /** A source shown in [category] at once; the server catches up (its list arrives before the mutation resolves). */
  const place = (id: string, category: string) => command(() => setCategory({ id: id as never, category }), failure);
  const moveSource = async (id: string, into: string) => {
    const f = feeds?.find((x) => x.id === id);
    if (!f || f.category === into) return;
    const back = f.category;
    if (!(await place(id, into)).ok) return;
    toast(`Moved ${f.title} to ${into}`, () => place(id, back), "folder");
  };
  const rename = async (from: string, raw: string) => {
    const to = raw.replace(/\s+/g, " ").trim();
    if (!to || to === from) return;
    const before = placed[placed.indexOf(from) + 1] ?? null;
    const merging = cats.includes(to);
    const s = getState().scope, context = currentViewContext();
    const outcome = await command(() => renameCat({ from, to }), failure);
    if (!outcome.ok) return;
    const result = outcome.value;
    if (result.outcome === "unchanged") return;
    if (result.outcome === "missing") { toast("That category no longer exists"); return; }
    const moved = result.moved;
    if (s.type === "category" && s.name === from) retargetView(context, { type: "category", name: to }, to);
    const undoContext = currentViewContext();
    toast(merging ? `Moved ${moved.length === 1 ? "1 source" : `${moved.length} sources`} into ${to}` : `Renamed ${from} to ${to}`, () => { void (async () => {
        const result = await command(() => undoRename({ ids: moved, from: to, to: from, before }), failure);
        if (!result.ok || !result.value) return;
        const now = getState().scope;
        if (now.type === "category" && now.name === to && !merging) retargetView(undoContext, { type: "category", name: from }, from);
    })();
    }, "folder");
  };
  return { reorder, moveSource, rename, placed };
}

/** Unknown stays unknown; an add safely reaches the server even before this query loads. */
export function useTopics(): string[] | undefined { return useSettings()?.topics; }

export function useTopicActions() {
  const topics = useTopics();
  const send = useOwnerMutation(api.settings.editTopic, optimisticTopic);
  const edit = (edit: TopicEdit) => command(() => send({ edit }), "Couldn't update topics · try again");
  const find = (name: string) => topics?.find((t) => topicKey(t) === topicKey(name));
  const viewing = (name: string) => { const s = getState().scope; return s.type === "topic" && topicKey(s.name) === topicKey(name); };
  const follow = (raw: string): string | null => {
    const name = topicName(raw);
    if (!name) return null;
    const had = find(name);
    if (had) return had;
    if (topics && topics.length >= MAX_TOPICS) { toast(`Up to ${MAX_TOPICS} topics · stop following one first`); return null; }
    edit({ type: "add", name }); return name;
  };
  const rename = (from: string, raw: string): string | null => {
    const name = topicName(raw);
    if (!name || name === from) return null;
    const other = find(name);
    if (other && topicKey(other) !== topicKey(from)) { toast(`Already following “${topicLabel(other)}”`); return null; }
    const context = currentViewContext(), wasViewing = viewing(from);
    void edit({ type: "rename", name: from, to: name }).then((result) => {
      if (!result.ok) return;
      const outcome = result.value;
      if (outcome === "missing") toast("That topic is no longer followed");
      if (outcome === "changed" && wasViewing) retargetView(context, { type: "topic", name }, topicLabel(name));
    });
    return name;
  };
  const unfollow = async (name: string) => {
    if (!topics?.includes(name)) return;
    const before = topics[topics.indexOf(name) + 1] ?? null;
    const result = await edit({ type: "remove", name });
    if (!result.ok || result.value !== "changed") return;
    if (viewing(name)) setView({ type: "all" });
    toast(`Stopped following “${topicLabel(name)}”`, () => edit({ type: "add", name, before }), "hash");
  };
  const move = (from: number, to: number) => {
    if (!topics || from === to || !topics[from] || to < 0 || to >= topics.length) return;
    const next = [...topics]; next.splice(to, 0, ...next.splice(from, 1));
    edit({ type: "move", name: topics[from]!, before: next[to + 1] ?? null });
  };
  return { follow, rename, unfollow, move };
}

const serverScope = (scope: Scope) =>
  scope.type === "category" ? { type: "category" as const, name: scope.name }
  : scope.type === "feed" ? { type: "feed" as const, feedId: scope.feedId as never }
  : scope.type === "topic" ? { type: "topic" as const, name: scope.name }
  : { type: "all" as const };

/** The rows of the current view, ordered and filtered exactly as displayed. */
/** The last rows each list showed, so going back to a list shows it at once while it refreshes. */


export function useListRows() {
  const prefs = usePrefs();
  const scope = useStore((st) => st.scope);
  const filter = useStore((st) => st.filter);
  const keep = useStore((st) => st.keep);
  const overlay = useStore((st) => st.overlay);
  const removed = useStore((st) => st.removed);
  const feeds = useFeeds();
  const listArgs = scope.type === "search" || scope.type === "recent" ? "skip" : { scope: serverScope(scope), filter, order: prefs.order };
  const pages = useOwnerPages(api.articles.page, listArgs, PAGE);
  const recent = useOwnerPages(api.articles.recent, scope.type === "recent" ? {} : "skip", 100);
  const searched = useOwnerQuery(api.articles.search, scope.type === "search" ? { query: scope.query } : "skip");

  const source = scope.type === "recent" ? recent : pages;
  const collectionKey = JSON.stringify([scope, filter, prefs.order]);
  const [progress, setProgress] = useState({ key: collectionKey, status: source.status, revision: 0 });
  const requestedPage = useRef<number | null>(null);
  // Update during this component's render so rows and their arrival kind commit together.
  // An effect would first publish the new rows as a live arrival, losing their page order.
  if (progress.key !== collectionKey) {
    requestedPage.current = null;
    setProgress({ key: collectionKey, status: source.status, revision: 0 });
  }
  const arrived = source.status !== "LoadingMore" && source.status !== "LoadingFirstPage" &&
    (progress.status === "LoadingMore" || (requestedPage.current !== null && (source.results.length > requestedPage.current || source.status === "Exhausted")));
  if (progress.status !== source.status || arrived) {
    if (arrived) requestedPage.current = null;
    setProgress({ key: collectionKey, status: source.status, revision: progress.revision + Number(arrived) });
  }
  const pageRevision = progress.revision;
  const loadMore = () => {
    if (source.status !== "CanLoadMore" || requestedPage.current !== null) return;
    requestedPage.current = source.results.length;
    source.loadMore(PAGE);
  };

  // Fill filtered views within the automatic budget; later pages always remain reachable.
  const loading = useRef(false);
  useEffect(() => {
    if (source.status === "CanLoadMore" && (filter !== "all" || scope.type === "recent") && source.results.length < AUTOLOAD_BUDGET && !loading.current) {
      loading.current = true; loadMore();
    }
    if (source.status !== "LoadingMore") loading.current = false;
  }, [source, filter, scope.type]);

  // a first page still on its way is "not known yet", never "empty"
  const fresh = (scope.type === "search" ? searched : source.status === "LoadingFirstPage" ? undefined : source.results) as Row[] | undefined;
  const memo = collectionKey;
  useEffect(() => { if (fresh) retainView(memo, fresh); }, [fresh, memo]);
  const base = fresh ?? viewSnapshot(memo);
  // Completion updates retained-row fallback metadata. Reapply cached server rows even
  // when their query result did not change, so authoritative rows always win.
  const liveFeeds = useMemo(() => feeds === undefined ? undefined : new Set<string>(feeds.filter((f) => !f.deleted).map((f) => f.id)), [feeds]);
  const visible = (r: Row) => { const a = withOverlay(r); return inLibrary(a.isSaved, liveFeeds !== undefined && !liveFeeds.has(a.feedId), a.retiredAt !== undefined); };
  useEffect(() => { if (fresh) remember(fresh.filter(visible), liveFeeds); }, [fresh, overlay, liveFeeds]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    if (!base) return undefined;
    const current = fresh === undefined ? base.map((r) => rowCache.get(r.id) ?? r) : base;
    const ids = new Set(current.map((r) => r.id));
    const kept = [...keep].filter((id) => !ids.has(id)).map((id) => rowCache.get(id)).filter((r): r is Row => !!r);
    const all = [...current, ...kept].filter((r) => !removed.has(r.id) && visible(r)).map(withOverlay);
    if (scope.type === "recent") return orderRecent(all);
    const filtered = scope.type === "search" ? all : applyFilter(all, filter, keep);
    return orderRows(filtered, prefs.order, scope.type === "feed" ? "time" : prefs.group);
    // overlay is read through withOverlay
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, fresh, keep, overlay, removed, liveFeeds, filter, prefs.order, prefs.group, scope]);

  return { rows, canLoadMore: source.status === "CanLoadMore", loadMore, complete: scope.type === "search" ? searched !== undefined : source.status === "Exhausted", loadingMore: source.status === "LoadingMore", pageRevision, key: collectionKey };
}

/** Read and save changes, applied instantly and confirmed by the server. */
export function useArticleActions() {
  const setRead = useOwnerMutation(api.articles.setRead);
  const setReadMany = useOwnerMutation(api.articles.setReadMany);
  const setSaved = useOwnerMutation(api.articles.setSaved);
  const markScope = useOwnerMutation(api.articles.markScopeReadPage);

  const perform = (ids: string[], change: { isRead?: boolean; isSaved?: boolean }, send: () => Promise<unknown>) => {
    const edits = ids.map((id) => beginArticleEdit(id, change));
    void command(send, "Couldn't update these articles · try again").then((result) => edits.forEach((e) => finishArticleEdit(e, result.ok)));
  };
  const read = (id: string, value: boolean) => {
    if (value) update({ keep: new Set(getState().keep).add(id) });
    perform([id], { isRead: value }, () => setRead({ id: id as never, value, at: Date.now() }));
  };
  const save = (id: string, value: boolean) => {
    perform([id], { isSaved: value }, () => setSaved({ id: id as never, value }));
    toast(value ? "Saved" : "Removed from Saved", () => save(id, !value), "bookmark");
  };
  /** Everything unread in this view, with Undo. */
  const markAll = async (label: string, place?: Scope) => {
    const scope = place ?? getState().scope;
    // Saved is a place to keep things, not a queue to clear: marking "all" there would reach every
    // unsaved article of the scope too. One rule here covers the button, Shift+A, the palette and a topic's menu.
    if (scope.type === "search" || scope.type === "recent" || (!place && getState().filter === "saved")) return;
    const ids: string[] = [], before = Date.now();
    let complete = false;
    while (!complete) {
      const result = await command(() => markScope({ scope: serverScope(scope), before }), "Couldn't finish marking this view read · try again");
      if (!result.ok) break;
      ids.push(...result.value.changed);
      complete = result.value.complete;
    }
    if (!complete && !ids.length) return;
    if (!ids.length) { toast(`Nothing unread in ${label}`); return; }
    for (const id of ids) { const r = rowCache.get(id); if (r) rowCache.set(id, { ...r, isRead: true }); }
    toast(`Marked ${ids.length} read in ${label}${complete ? "" : " · stopped early"}`, () => {
      // the server takes at most 500 per call
      for (let i = 0; i < ids.length; i += 400) {
        const part = ids.slice(i, i + 400);
        perform(part, { isRead: false }, () => setReadMany({ ids: part as never, value: false }));
      }
    }, "checks");
  };
  /** Mark many rows read or unread at once, with Undo. Marked-read rows stay in view until the list is left. */
  const readMany = (ids: string[], value: boolean) => {
    const changed = ids.filter((id) => { const r = rowCache.get(id); return r && withOverlay(r).isRead !== value; });
    if (!changed.length) return;
    const apply = (v: boolean) => {
      if (v) { const keep = new Set(getState().keep); for (const id of changed) keep.add(id); update({ keep }); }
      for (let i = 0; i < changed.length; i += 400) {
        const part = changed.slice(i, i + 400);
        perform(part, { isRead: v }, () => setReadMany({ ids: part as never, value: v }));
      }
    };
    apply(value);
    toast(`Marked ${changed.length} ${value ? "read" : "unread"}`, () => apply(!value), value ? "checks" : "circleDot");
  };
  return { read, save, markAll, readMany, setReadMany };
}

export const readingLabel = (scope: Scope, feeds: Feed[] | undefined) =>
  scope.type === "all" ? "All sources"
  : scope.type === "category" ? scope.name
  : scope.type === "feed" ? feeds?.find((f) => f.id === scope.feedId)?.title ?? "Source"
  : scope.type === "topic" ? topicLabel(scope.name)
  : scope.type === "search" ? `“${scope.query}”`
  : "Recently read";

